import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Field } from "../components/FormField";
import BookingDetailDialog from "../components/BookingDetailDialog";
import ApartmentCheckboxFilter from "../components/ApartmentCheckboxFilter";
import { CHANNELS, PAY_STATUS, DOC_STATUS, eur } from "../helpers";
import { ChevronLeft, ChevronRight, Sparkles, Wrench, Users, LogIn, LogOut, Plus, Lock, Trash2, Ban } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const pd = (s) => new Date(s + "T00:00:00");
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const between = (a, b) => Math.round((pd(b) - pd(a)) / 86400000);
const todayStr = ymd(new Date());

const CHANNEL_META = {
  airbnb: { label: "Airbnb", bg: "#FF5A5F" },
  booking: { label: "Booking.com", bg: "#1E40AF" },
  diretto: { label: "Sito diretto", bg: "#0D9488" },
  altro: { label: "Manuale", bg: "#64748B" },
};
const CLEAN_DOT = { da_fare: "#94a3b8", in_corso: "#2563eb", completata: "#10b981" };
const SOURCE_META = {
  krossbooking: { label: "KrossBooking", color: "#10B981" },
  airbnb: { label: "Airbnb", color: "#3B82F6" },
  booking: { label: "Booking.com", color: "#A855F7" },
  diretto: { label: "Diretto", color: "#F97316" },
  manuale: { label: "Manuale", color: "#4B5563" },
};
const SOURCES = ["krossbooking", "airbnb", "booking", "diretto", "manuale"];
const bookingSource = (b) => b.source || ({ airbnb: "airbnb", booking: "booking", diretto: "diretto", altro: "manuale" }[b.channel] || "manuale");

const BLOCK_LABELS = {
  uso_proprietario: "Uso proprietario", uso_urban: "Uso Urban Dreams", manutenzione: "Manutenzione",
  pulizia_straordinaria: "Pulizia straordinaria", fuori_servizio: "Fuori servizio", shooting: "Shooting fotografico",
  restyling: "Restyling", manuale: "Blocco manuale", emergenza: "Emergenza",
};
const emptyBlock = (aptId, sd) => ({
  apartment_id: aptId || "", start_date: sd, end_date: ymd(addDays(pd(sd), 1)),
  reason_type: "manuale", reason: "", notes: "", counts_as_occupied: false,
});

const emptyRes = (aptId, ci) => ({
  apartment_id: aptId || "", guest_name: "", guests: 1, check_in: ci, check_out: ymd(addDays(pd(ci), 1)),
  channel: "diretto", total_amount: 0, city_tax: 0, paid_amount: 0, payment_method: "OTA",
  payment_status: "in_sospeso", document_status: "mancante", checkin_instructions_status: "da_inviare", notes: "",
});

export default function Calendar() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [view, setView] = useState("month");
  const [ref, setRef] = useState(() => new Date());
  const [data, setData] = useState({ apartments: [], bookings: [], cleanings: [], maintenance: [], owners: [], blocks: [], block_types: [], can_edit_bookings: false, can_manage_blocks: false, can_override_block: false, user_email: "" });
  const [fullBookings, setFullBookings] = useState([]);
  const [selApts, setSelApts] = useState([]);
  const [fOwner, setFOwner] = useState("all");
  const [fChannel, setFChannel] = useState("all");
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [blkOpen, setBlkOpen] = useState(false);
  const [blkForm, setBlkForm] = useState(null);
  const [blkSaving, setBlkSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    api.get("/calendar/data").then((r) => {
      setData(r.data);
      if (r.data.can_edit_bookings) api.get("/bookings").then((b) => setFullBookings(b.data)).catch(() => {});
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const canEdit = data.can_edit_bookings;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ---- filters
  let apartments = data.apartments;
  if (selApts.length) apartments = apartments.filter((a) => selApts.includes(a.id));
  if (fOwner !== "all") apartments = apartments.filter((a) => a.owner_id === fOwner);
  const aptIds = new Set(apartments.map((a) => a.id));
  let bookings = data.bookings.filter((b) => aptIds.has(b.apartment_id));
  if (fChannel !== "all") bookings = bookings.filter((b) => b.channel === fChannel);
  const cleanings = data.cleanings.filter((c) => aptIds.has(c.apartment_id));
  const maintenance = data.maintenance.filter((m) => aptIds.has(m.apartment_id) && m.status !== "risolto");
  const blocks = (data.blocks || []).filter((b) => aptIds.has(b.apartment_id));

  // ---- period
  let days = [];
  if (view === "month") {
    const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
    const n = new Date(ref.getFullYear(), ref.getMonth() + 1, 0).getDate();
    days = Array.from({ length: n }, (_, i) => addDays(first, i));
  } else if (view === "week") {
    const dow = (ref.getDay() + 6) % 7; // monday start
    const start = addDays(ref, -dow);
    days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  } else {
    days = [new Date(ref)];
  }
  const periodStart = ymd(days[0]);
  const periodEnd = ymd(addDays(days[days.length - 1], 1));
  const colW = view === "week" ? 96 : view === "day" ? 260 : 46;

  const periodLabel = view === "day"
    ? ref.toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
    : view === "week"
      ? `${days[0].toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { day: "2-digit", month: "short" })} – ${days[6].toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { day: "2-digit", month: "short", year: "numeric" })}`
      : ref.toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { month: "long", year: "numeric" });

  const move = (delta) => {
    const d = new Date(ref);
    if (view === "month") d.setMonth(d.getMonth() + delta);
    else if (view === "week") d.setDate(d.getDate() + delta * 7);
    else d.setDate(d.getDate() + delta);
    setRef(d);
  };

  // ---- interactions
  const openCreate = (aptId, dateStr) => {
    if (!canEdit) return;
    setForm(emptyRes(aptId, dateStr)); setDlgOpen(true);
  };
  const openBooking = (b) => {
    setDetailId(b.id); setDetailOpen(true);
  };
  const openEdit = (b) => {
    const full = fullBookings.find((x) => x.id === b.id);
    setForm(full ? { ...full } : { ...b, _readonly: !canEdit });
    setDlgOpen(true);
  };

  const save = async () => {
    if (!form.apartment_id || !form.guest_name?.trim()) { toast.error(t("guestName")); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/bookings/${form.id}`, form);
      else await api.post("/bookings", form);
      toast.success(t("saved")); setDlgOpen(false); load();
    } catch (e) {
      if (e.response?.status === 409 && data.can_override_block) {
        const reason = window.prompt(`${e.response.data?.detail}\n\nSei Super Admin: inserisci una motivazione per forzare la prenotazione:`);
        if (reason && reason.trim()) {
          try {
            const q = `?override=true&override_reason=${encodeURIComponent(reason.trim())}`;
            if (form.id) await api.put(`/bookings/${form.id}${q}`, form);
            else await api.post(`/bookings${q}`, form);
            toast.success("Prenotazione forzata sul blocco"); setDlgOpen(false); load();
          } catch (e2) { toast.error(e2.response?.data?.detail || "Error"); }
        }
      } else {
        toast.error(e.response?.data?.detail || "Error");
      }
    }
    finally { setSaving(false); }
  };

  // ---- blocks ----
  const openBlockCreate = (aptId, dateStr) => {
    if (!data.can_manage_blocks) return;
    setBlkForm(emptyBlock(aptId || apartments[0]?.id || "", dateStr || todayStr)); setBlkOpen(true);
  };
  const openBlockEdit = (blk) => { setBlkForm({ ...blk }); setBlkOpen(true); };
  const canManageThisBlock = (blk) => {
    if (!blk?.id) return true;
    return ["super_admin", "admin", "amministratore"].includes(user?.role) || blk.created_by === data.user_email;
  };
  const saveBlock = async () => {
    if (!blkForm.apartment_id) { toast.error(t("selectApartment")); return; }
    if (blkForm.end_date <= blkForm.start_date) { toast.error("La data di fine deve essere dopo l'inizio"); return; }
    setBlkSaving(true);
    try {
      if (blkForm.id) await api.put(`/apartment-blocks/${blkForm.id}`, blkForm);
      else await api.post("/apartment-blocks", blkForm);
      toast.success(t("saved")); setBlkOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBlkSaving(false); }
  };
  const deleteBlock = async () => {
    if (!blkForm?.id || !window.confirm("Eliminare questo blocco?")) return;
    try { await api.delete(`/apartment-blocks/${blkForm.id}`); toast.success(t("deleted")); setBlkOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onDrop = async (aptId, dateStr, e) => {
    e.preventDefault();
    if (!canEdit) return;
    const bid = e.dataTransfer.getData("bookingId");
    const full = fullBookings.find((x) => x.id === bid);
    if (!full) return;
    const nights = Math.max(between(full.check_in, full.check_out), 1);
    const newCi = dateStr;
    const newCo = ymd(addDays(pd(dateStr), nights));
    try {
      await api.put(`/bookings/${bid}`, { ...full, apartment_id: aptId, check_in: newCi, check_out: newCo });
      toast.success(t("saved")); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const cleaningFor = (aptId, dateStr) => cleanings.find((c) => c.apartment_id === aptId && c.date === dateStr);
  const maintCountFor = (aptId) => maintenance.filter((m) => m.apartment_id === aptId).length;

  return (
    <div className="space-y-4 fade-in" data-testid="calendar-page">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button data-testid="cal-prev" variant="outline" size="icon" onClick={() => move(-1)}><ChevronLeft size={18} /></Button>
          <Button data-testid="cal-today" variant="outline" size="sm" onClick={() => setRef(new Date())}>{t("todayBtn")}</Button>
          <Button data-testid="cal-next" variant="outline" size="icon" onClick={() => move(1)}><ChevronRight size={18} /></Button>
          <h2 className="ml-2 font-head text-lg font-semibold capitalize text-slate-900">{periodLabel}</h2>
        </div>
        <div className="flex items-center gap-2">
          {data.can_manage_blocks && (
            <Button data-testid="add-block-btn" variant="outline" size="sm" className="gap-1.5 border-slate-300 text-slate-700" onClick={() => openBlockCreate(fApt !== "all" ? fApt : "", todayStr)}>
              <Lock size={15} /> Blocca date
            </Button>
          )}
          <div className="flex items-center rounded-lg border border-border bg-white p-0.5">
            {["month", "week", "day"].map((v) => (
              <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100"}`}>
                {t(v + "View")}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters + legend */}
      <div className="flex flex-wrap items-center gap-3">
        <ApartmentCheckboxFilter apartments={data.apartments} selected={selApts} onChange={setSelApts} testid="filter-apartments" />
        <Select value={fOwner} onValueChange={setFOwner}>
          <SelectTrigger data-testid="filter-owner" className="h-9 w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("allOwners")}</SelectItem>
            {data.owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fChannel} onValueChange={setFChannel}>
          <SelectTrigger data-testid="filter-channel" className="h-9 w-40"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("allChannels")}</SelectItem>
            {CHANNELS.map((c) => <SelectItem key={c} value={c}>{CHANNEL_META[c].label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-auto flex flex-wrap items-center gap-3 text-xs text-slate-500">
          <span className="font-medium text-slate-400">Provenienza:</span>
          {SOURCES.map((s) => (
            <span key={s} data-testid={`legend-source-${s}`} className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-white/70" style={{ background: SOURCE_META[s].color }} /> {SOURCE_META[s].label}</span>
          ))}
          <span className="flex items-center gap-1"><Sparkles size={13} className="text-cyan-600" /> {t("housekeeping")}</span>
          <span className="flex items-center gap-1"><Wrench size={13} className="text-amber-600" /> {t("maintenanceBlocks")}</span>
          <span className="flex items-center gap-1"><Lock size={13} className="text-slate-500" /> Blocco</span>
        </div>
      </div>

      {view === "day" ? (
        <DayView apartments={apartments} bookings={bookings} cleanings={cleanings} maintenance={maintenance}
          dateStr={ymd(ref)} t={t} onOpenBooking={openBooking} onCreate={openCreate} canEdit={canEdit} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-white" data-testid="calendar-grid">
          <div style={{ minWidth: 160 + days.length * colW }}>
            {/* header */}
            <div className="flex border-b border-border bg-secondary/50">
              <div className="sticky left-0 z-10 shrink-0 border-r border-border bg-secondary/50 px-3 py-2 text-xs font-semibold text-slate-600" style={{ width: 160 }}>{t("apartment")}</div>
              {days.map((d) => {
                const isToday = ymd(d) === todayStr;
                const we = [0, 6].includes(d.getDay());
                return (
                  <div key={ymd(d)} className={`shrink-0 py-2 text-center ${isToday ? "bg-primary text-primary-foreground" : we ? "bg-slate-50 text-slate-500" : "text-slate-500"}`} style={{ width: colW }}>
                    <div className="text-[10px] uppercase">{d.toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { weekday: "short" })}</div>
                    <div className="text-sm font-semibold">{d.getDate()}</div>
                  </div>
                );
              })}
            </div>
            {/* rows */}
            {apartments.map((a) => {
              const rowBookings = bookings.filter((b) => b.apartment_id === a.id && b.check_out > periodStart && b.check_in < periodEnd);
              const rowBlocks = blocks.filter((b) => b.apartment_id === a.id && b.end_date > periodStart && b.start_date < periodEnd);
              const mCount = maintCountFor(a.id);
              return (
                <div key={a.id} data-testid={`cal-row-${a.id}`} className="flex border-b border-slate-100">
                  <div className="sticky left-0 z-10 flex shrink-0 items-center justify-between gap-2 border-r border-border bg-white px-3 py-2" style={{ width: 160 }}>
                    <span className="truncate text-sm font-medium text-slate-800">{a.name}</span>
                    {mCount > 0 && <span title={t("maintenanceBlocks")} className="flex items-center gap-0.5 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700"><Wrench size={10} /> {mCount}</span>}
                  </div>
                  <div className="relative" style={{ width: days.length * colW, height: 52 }}>
                    {/* day cells (drop targets + create) */}
                    <div className="flex h-full">
                      {days.map((d) => {
                        const ds = ymd(d);
                        const cl = cleaningFor(a.id, ds);
                        const we = [0, 6].includes(d.getDay());
                        return (
                          <div key={ds} data-testid={`cal-cell-${a.id}-${ds}`}
                            onClick={() => openCreate(a.id, ds)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => onDrop(a.id, ds, e)}
                            className={`relative shrink-0 border-r border-slate-100 ${we ? "bg-slate-50/60" : ""} ${canEdit ? "cursor-pointer hover:bg-primary/5" : ""}`}
                            style={{ width: colW }}>
                            {cl && <span title={`${t("housekeeping")}: ${cl.staff || ""}`} className="absolute bottom-1 right-1 h-2 w-2 rounded-full" style={{ background: CLEAN_DOT[cl.status] || "#94a3b8" }} />}
                          </div>
                        );
                      })}
                    </div>
                    {/* block bars */}
                    {rowBlocks.map((blk) => {
                      const bStart = blk.start_date < periodStart ? periodStart : blk.start_date;
                      const bEnd = blk.end_date > periodEnd ? periodEnd : blk.end_date;
                      const startIdx = between(periodStart, bStart);
                      const span = Math.max(between(bStart, bEnd), 1);
                      const color = blk.color || "#475569";
                      return (
                        <div key={blk.id} data-testid={`cal-block-${blk.id}`}
                          onClick={(e) => { e.stopPropagation(); openBlockEdit(blk); }}
                          title={`${BLOCK_LABELS[blk.reason_type] || "Blocco"} · ${blk.start_date} → ${blk.end_date}${blk.reason ? " · " + blk.reason : ""}`}
                          className="absolute bottom-1 flex items-center gap-1 overflow-hidden rounded-md px-1.5 text-[10px] font-semibold text-white shadow-sm"
                          style={{
                            left: startIdx * colW + 2, width: span * colW - 4, height: 14, cursor: "pointer",
                            backgroundColor: color,
                            backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.25) 0, rgba(255,255,255,0.25) 3px, transparent 3px, transparent 7px)",
                          }}>
                          <Lock size={9} className="shrink-0" />
                          {span * colW >= 70 && <span className="truncate">{BLOCK_LABELS[blk.reason_type] || "Blocco"}</span>}
                        </div>
                      );
                    })}
                    {/* booking bars */}
                    {rowBookings.map((b) => {
                      const bStart = b.check_in < periodStart ? periodStart : b.check_in;
                      const bEnd = b.check_out > periodEnd ? periodEnd : b.check_out;
                      const startIdx = between(periodStart, bStart);
                      const span = Math.max(between(bStart, bEnd), 1);
                      const meta = CHANNEL_META[b.channel] || CHANNEL_META.altro;
                      return (
                        <div key={b.id} data-testid={`cal-booking-${b.id}`} draggable={canEdit}
                          onDragStart={(e) => e.dataTransfer.setData("bookingId", b.id)}
                          onClick={(e) => { e.stopPropagation(); openBooking(b); }}
                          title={`${b.guest_name} · ${b.check_in} → ${b.check_out} · ${SOURCE_META[bookingSource(b)].label}`}
                          className="absolute top-1.5 flex items-center gap-1 overflow-hidden rounded-md px-2 text-[11px] font-medium text-white shadow-sm transition-transform hover:z-20 hover:scale-[1.02]"
                          style={{ left: startIdx * colW + 2, width: span * colW - 4, height: 36, background: meta.bg, cursor: canEdit ? "grab" : "pointer" }}>
                          <span data-testid={`cal-booking-source-${b.id}`} className="h-2 w-2 shrink-0 rounded-full ring-1 ring-white/80" style={{ background: SOURCE_META[bookingSource(b)].color }} />
                          {span * colW >= 58 && <span className="truncate">{b.guest_name}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {apartments.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-400">{t("noData")}</div>}
          </div>
        </div>
      )}

      <ReservationDialog open={dlgOpen} onOpenChange={setDlgOpen} form={form} set={set} save={save}
        apartments={data.apartments} t={t} canEdit={canEdit} saving={saving} />

      <BlockDialog open={blkOpen} onOpenChange={setBlkOpen} form={blkForm} setForm={setBlkForm}
        apartments={data.apartments} blockTypes={data.block_types} save={saveBlock} del={deleteBlock}
        saving={blkSaving} canManage={canManageThisBlock(blkForm)} t={t} />

      <BookingDetailDialog open={detailOpen} onOpenChange={setDetailOpen} bookingId={detailId}
        canEdit={canEdit} onEdit={openEdit} onBlockDates={openBlockCreate} />
    </div>
  );
}

function BlockDialog({ open, onOpenChange, form, setForm, apartments, blockTypes, save, del, saving, canManage, t }) {
  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onTypeChange = (v) => {
    const meta = (blockTypes || []).find((x) => x.key === v);
    setForm((f) => ({ ...f, reason_type: v, counts_as_occupied: meta ? meta.default_occ : f.counts_as_occupied }));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="block-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? "Modifica blocco" : "Nuovo blocco date"}</DialogTitle>
          <DialogDescription>Blocca l'appartamento nel calendario (escluso da nuove prenotazioni).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Appartamento" className="col-span-2">
            <Select value={form.apartment_id} onValueChange={(v) => set("apartment_id", v)} disabled={!canManage}>
              <SelectTrigger data-testid="block-apartment"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Data inizio"><Input data-testid="block-start" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} disabled={!canManage} /></Field>
          <Field label="Data fine"><Input data-testid="block-end" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} disabled={!canManage} /></Field>
          <Field label="Motivo (tipo)" className="col-span-2">
            <Select value={form.reason_type} onValueChange={onTypeChange} disabled={!canManage}>
              <SelectTrigger data-testid="block-type"><SelectValue /></SelectTrigger>
              <SelectContent>{(blockTypes || []).map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Descrizione" className="col-span-2"><Input data-testid="block-reason" value={form.reason} onChange={(e) => set("reason", e.target.value)} disabled={!canManage} placeholder="Es. richiesta proprietario, guasto…" /></Field>
          <Field label="Note interne" className="col-span-2"><Textarea data-testid="block-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} disabled={!canManage} /></Field>
          <div className="col-span-2 flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-2.5">
            <div>
              <div className="text-sm font-medium text-slate-700">Conta come occupato</div>
              <div className="text-xs text-slate-500">Impatta solo le statistiche di occupazione (no ricavi/ADR/RevPAR)</div>
            </div>
            <Switch data-testid="block-occ" checked={!!form.counts_as_occupied} onCheckedChange={(v) => set("counts_as_occupied", v)} disabled={!canManage} />
          </div>
        </div>
        {canManage && (
          <DialogFooter className="flex-row justify-between sm:justify-between">
            <div>
              {form.id && <Button variant="outline" className="gap-1 border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="delete-block-btn" onClick={del}><Trash2 size={15} /> Elimina</Button>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
              <Button data-testid="save-block-btn" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90">{saving ? t("saving") : t("save")}</Button>
            </div>
          </DialogFooter>
        )}
        {!canManage && (
          <div className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700"><Ban size={14} /> Blocco creato da un amministratore: sola lettura.</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DayView({ apartments, bookings, cleanings, maintenance, dateStr, t, onOpenBooking, onCreate, canEdit }) {
  const info = (aptId) => {
    const b = bookings.find((x) => x.apartment_id === aptId && x.check_in <= dateStr && x.check_out > dateStr);
    if (!b) return { type: "free" };
    if (b.check_in === dateStr) return { type: "arrival", b };
    return { type: "stayover", b };
  };
  const dep = (aptId) => bookings.find((x) => x.apartment_id === aptId && x.check_out === dateStr);
  const TYPE = {
    arrival: { icon: LogIn, cls: "bg-emerald-100 text-emerald-700", label: t("arrival") },
    departure: { icon: LogOut, cls: "bg-blue-100 text-blue-700", label: t("departure") },
    stayover: { icon: Users, cls: "bg-violet-100 text-violet-700", label: t("stayover") },
  };
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="calendar-dayview">
      {apartments.map((a) => {
        const i = info(a.id); const d = dep(a.id);
        const cl = cleanings.find((c) => c.apartment_id === a.id && c.date === dateStr);
        const mts = maintenance.filter((m) => m.apartment_id === a.id);
        return (
          <div key={a.id} data-testid={`day-apt-${a.id}`} className="rounded-lg border border-border bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-head font-semibold text-slate-900">{a.name}</h3>
              {canEdit && i.type === "free" && (
                <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={() => onCreate(a.id, dateStr)}><Plus size={12} /> {t("add")}</Button>
              )}
            </div>
            <div className="space-y-2">
              {i.type !== "free" && (
                <button onClick={() => onOpenBooking(i.b)} className={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm ${TYPE[i.type].cls}`}>
                  {(() => { const Ic = TYPE[i.type].icon; return <Ic size={14} />; })()}
                  <span className="font-medium">{TYPE[i.type].label}</span>· {i.b.guest_name}
                </button>
              )}
              {d && d.id !== (i.b && i.b.id) && (
                <button onClick={() => onOpenBooking(d)} className={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm ${TYPE.departure.cls}`}>
                  <LogOut size={14} /> <span className="font-medium">{TYPE.departure.label}</span>· {d.guest_name}
                </button>
              )}
              {i.type === "free" && !d && <p className="text-sm text-slate-400">{t("free")}</p>}
              {cl && <div className="flex items-center gap-2 text-xs text-slate-600"><Sparkles size={13} className="text-cyan-600" /> {t(cl.status === "da_fare" ? "todo" : cl.status === "in_corso" ? "inProgress" : "completed")} {cl.staff && `· ${cl.staff}`}</div>}
              {mts.map((m) => <div key={m.id} className="flex items-center gap-2 text-xs text-amber-700"><Wrench size={13} /> {m.issue}</div>)}
            </div>
          </div>
        );
      })}
      {apartments.length === 0 && <p className="text-sm text-slate-400">{t("noData")}</p>}
    </div>
  );
}

function ReservationDialog({ open, onOpenChange, form, set, save, apartments, t, canEdit, saving }) {
  if (!form) return null;
  const ro = form._readonly || !canEdit;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{form.id ? t("reservationDetails") : t("newReservation")}</DialogTitle>
          <DialogDescription>{t("bookings")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("apartment")} className="col-span-2">
            <Select value={form.apartment_id} onValueChange={(v) => set("apartment_id", v)} disabled={ro}>
              <SelectTrigger data-testid="res-apartment"><SelectValue placeholder={t("selectApartment")} /></SelectTrigger>
              <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("guestName")}><Input data-testid="res-guest" value={form.guest_name} onChange={(e) => set("guest_name", e.target.value)} disabled={ro} /></Field>
          <Field label={t("guests")}><Input type="number" value={form.guests} onChange={(e) => set("guests", +e.target.value)} disabled={ro} /></Field>
          <Field label="Telefono ospite"><Input data-testid="res-phone" value={form.guest_phone || ""} onChange={(e) => set("guest_phone", e.target.value)} disabled={ro} /></Field>
          <Field label="WhatsApp ospite"><Input data-testid="res-whatsapp" placeholder="+39…" value={form.guest_whatsapp || ""} onChange={(e) => set("guest_whatsapp", e.target.value)} disabled={ro} /></Field>
          <Field label="Email ospite" className="col-span-2"><Input data-testid="res-email" value={form.guest_email || ""} onChange={(e) => set("guest_email", e.target.value)} disabled={ro} /></Field>
          <Field label={t("checkIn")}><Input data-testid="res-checkin" type="date" value={form.check_in} onChange={(e) => set("check_in", e.target.value)} disabled={ro} /></Field>
          <Field label={t("checkOut")}><Input data-testid="res-checkout" type="date" value={form.check_out} onChange={(e) => set("check_out", e.target.value)} disabled={ro} /></Field>
          <Field label={t("channel")}>
            <Select value={form.channel} onValueChange={(v) => set("channel", v)} disabled={ro}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{CHANNEL_META[c].label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {form.total_amount !== undefined && (
            <Field label={t("totalAmount")}><Input type="number" value={form.total_amount} onChange={(e) => set("total_amount", +e.target.value)} disabled={ro} /></Field>
          )}
          {form.payment_status !== undefined && (
            <Field label={t("paymentStatus")}>
              <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)} disabled={ro}>
                <SelectTrigger data-testid="res-paystatus"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_STATUS.map((c) => <SelectItem key={c} value={c}>{t({ pagato: "paid", parziale: "partial", in_sospeso: "pending" }[c])}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          {form.document_status !== undefined && (
            <Field label={t("docStatus")}>
              <Select value={form.document_status} onValueChange={(v) => set("document_status", v)} disabled={ro}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_STATUS.map((c) => <SelectItem key={c} value={c}>{t({ ricevuto: "received", mancante: "missing" }[c])}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
        </div>
        {!ro && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
            <Button data-testid="save-reservation-btn" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90">{saving ? t("saving") : t("save")}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
